import { useState, useMemo } from "react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { useProfiles } from "@/hooks/auth/useProfiles";
import { useQuickCreateGenies } from "@/hooks/admin/useQuickCreateGenies";
import { industries } from "@/data/discovery/industries";
import {
  getAllPublicGenies,
  getPublicGeniesForIndustry,
  getPublicGeniesForSubcategory,
  searchPublicGenies,
} from "@/data/discovery/genie-catalog";
import type { PublicGeniePersona } from "@/types/discovery-catalog";
import { Search, ChevronLeft, ChevronRight, User, Zap, Check, X, Loader2, ChevronDown } from "lucide-react";

type Step = "personas" | "confirm";

interface QuickCreateSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const QuickCreateSheet = ({ isOpen, onClose, onSuccess }: QuickCreateSheetProps) => {
  // Step state
  const [step, setStep] = useState<Step>("personas");

  // Customer selection
  const [targetUserId, setTargetUserId] = useState<string>("");
  const [customerSearch, setCustomerSearch] = useState("");
  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false);

  // Persona step
  const [selectedIndustry, setSelectedIndustry] = useState<string>("");
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>("");
  const [personaSearch, setPersonaSearch] = useState("");
  const [selectedPersonas, setSelectedPersonas] = useState<Set<string>>(new Set());

  // Confirm step
  const [businessName, setBusinessName] = useState("");
  const [nameOverrides, setNameOverrides] = useState<Record<string, string>>({});

  // Hooks
  const { data: users } = useProfiles();
  const { createGenies, isCreating, progress, resetProgress } = useQuickCreateGenies({
    onComplete: onSuccess,
  });

  // Derived data
  const filteredUsers = useMemo(() => {
    if (!users) return [];
    if (!customerSearch) return users;
    const q = customerSearch.toLowerCase();
    return users.filter(
      (u) =>
        u.full_name?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q)
    );
  }, [users, customerSearch]);

  const displayedPersonas = useMemo((): PublicGeniePersona[] => {
    if (personaSearch) {
      return searchPublicGenies(personaSearch);
    }
    if (selectedSubcategory) {
      return getPublicGeniesForSubcategory(selectedSubcategory);
    }
    if (selectedIndustry) {
      return getPublicGeniesForIndustry(selectedIndustry);
    }
    return getAllPublicGenies();
  }, [selectedIndustry, selectedSubcategory, personaSearch]);

  const currentIndustry = useMemo(
    () => industries.find((i) => i.id === selectedIndustry),
    [selectedIndustry]
  );

  const selectedPersonasList = useMemo(
    () => getAllPublicGenies().filter((g) => selectedPersonas.has(g.id)),
    [selectedPersonas]
  );

  const selectedUserLabel = useMemo(() => {
    if (!targetUserId) return "My Account";
    const user = users?.find((u) => u.id === targetUserId);
    return user?.full_name || user?.email || "Selected User";
  }, [targetUserId, users]);

  // Handlers
  const handleReset = () => {
    setStep("personas");
    setTargetUserId("");
    setCustomerSearch("");
    setIsCustomerDropdownOpen(false);
    setSelectedIndustry("");
    setSelectedSubcategory("");
    setPersonaSearch("");
    setSelectedPersonas(new Set());
    setBusinessName("");
    setNameOverrides({});
    resetProgress();
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const togglePersona = (id: string) => {
    setSelectedPersonas((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleCreate = async () => {
    const items = selectedPersonasList.map((p) => ({
      personaId: p.id,
      customName: nameOverrides[p.id] || undefined,
    }));

    await createGenies({
      items,
      targetUserId: targetUserId || undefined,
      businessName: businessName || undefined,
    });
  };

  // =========================================================================
  // Render helpers
  // =========================================================================

  const renderHeader = () => {
    const titles: Record<Step, string> = {
      personas: "Quick Create Genies",
      confirm: "Review & Create",
    };
    const subtitles: Record<Step, string> = {
      personas: `${selectedPersonas.size} selected · for ${selectedUserLabel}`,
      confirm: `Creating ${selectedPersonas.size} genie${selectedPersonas.size !== 1 ? "s" : ""} for ${selectedUserLabel}`,
    };

    return (
      <div className="flex items-center justify-between border-b p-6 sm:p-8">
        <div className="flex items-center gap-3">
          {step === "confirm" && (
            <button
              onClick={() => setStep("personas")}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={isCreating}
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
          )}
          <div>
            <h2 className="text-xl font-bold text-gray-900">{titles[step]}</h2>
            <p className="text-sm text-gray-500 mt-1">{subtitles[step]}</p>
          </div>
        </div>
        <button
          onClick={handleClose}
          className="text-gray-400 hover:text-gray-600 text-2xl font-light transition-colors"
          disabled={isCreating}
        >
          &times;
        </button>
      </div>
    );
  };

  const renderCustomerSelector = () => (
    <div className="relative">
      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">
        Create for
      </label>
      <button
        onClick={() => setIsCustomerDropdownOpen(!isCustomerDropdownOpen)}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 bg-white hover:border-genie-primary/50 transition-colors text-left"
      >
        <User className="h-4 w-4 text-gray-400 flex-shrink-0" />
        <span className="text-sm font-medium text-gray-900 flex-1 truncate">
          {selectedUserLabel}
        </span>
        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isCustomerDropdownOpen ? "rotate-180" : ""}`} />
      </button>

      {isCustomerDropdownOpen && (
        <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-hidden">
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input
                placeholder="Search customers..."
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-genie-primary"
                autoFocus
              />
            </div>
          </div>
          <div className="overflow-y-auto max-h-48">
            <button
              onClick={() => { setTargetUserId(""); setIsCustomerDropdownOpen(false); setCustomerSearch(""); }}
              className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 transition-colors ${!targetUserId ? "bg-genie-primary/5" : ""}`}
            >
              <User className="h-3.5 w-3.5 text-genie-primary flex-shrink-0" />
              <span className="text-sm font-medium text-gray-900">My Account</span>
              {!targetUserId && <Check className="h-3.5 w-3.5 text-genie-primary ml-auto" />}
            </button>
            {filteredUsers.map((user) => (
              <button
                key={user.id}
                onClick={() => { setTargetUserId(user.id); setIsCustomerDropdownOpen(false); setCustomerSearch(""); }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 transition-colors ${targetUserId === user.id ? "bg-genie-primary/5" : ""}`}
              >
                <User className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">{user.full_name || "No name"}</p>
                  <p className="text-xs text-gray-500 truncate">{user.email}</p>
                </div>
                {targetUserId === user.id && <Check className="h-3.5 w-3.5 text-genie-primary flex-shrink-0" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderPersonaStep = () => (
    <div className="space-y-4">
      {/* Customer selector + Search row */}
      <div className="flex gap-3">
        <div className="w-56 flex-shrink-0">
          {renderCustomerSelector()}
        </div>
        <div className="flex-1">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">
            Search
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search genies by name, role, or tag..."
              value={personaSearch}
              onChange={(e) => {
                setPersonaSearch(e.target.value);
                if (e.target.value) {
                  setSelectedIndustry("");
                  setSelectedSubcategory("");
                }
              }}
              className="pl-10"
            />
          </div>
        </div>
      </div>

      {/* Industry chips */}
      {!personaSearch && (
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => {
              setSelectedIndustry("");
              setSelectedSubcategory("");
            }}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              !selectedIndustry
                ? "bg-genie-primary text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            All
          </button>
          {industries.map((ind) => (
            <button
              key={ind.id}
              onClick={() => {
                setSelectedIndustry(ind.id);
                setSelectedSubcategory("");
              }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                selectedIndustry === ind.id
                  ? "bg-genie-primary text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {ind.name}
            </button>
          ))}
        </div>
      )}

      {/* Subcategory chips */}
      {!personaSearch && currentIndustry && (
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedSubcategory("")}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              !selectedSubcategory
                ? "bg-genie-secondary/20 text-genie-secondary"
                : "bg-gray-50 text-gray-500 hover:bg-gray-100"
            }`}
          >
            All {currentIndustry.name}
          </button>
          {currentIndustry.subcategories.map((sub) => (
            <button
              key={sub.id}
              onClick={() => setSelectedSubcategory(sub.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                selectedSubcategory === sub.id
                  ? "bg-genie-secondary/20 text-genie-secondary"
                  : "bg-gray-50 text-gray-500 hover:bg-gray-100"
              }`}
            >
              {sub.name}
            </button>
          ))}
        </div>
      )}

      {/* Persona grid */}
      <div className="space-y-2">
        {displayedPersonas.length === 0 && (
          <p className="text-sm text-gray-500 text-center py-8">No genies found</p>
        )}
        {displayedPersonas.map((persona) => {
          const isSelected = selectedPersonas.has(persona.id);
          return (
            <button
              key={persona.id}
              onClick={() => togglePersona(persona.id)}
              className={`w-full flex items-start gap-3 p-3 rounded-lg border-2 transition-colors text-left ${
                isSelected
                  ? "border-genie-primary bg-genie-primary/5"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <Checkbox
                checked={isSelected}
                className="mt-0.5 pointer-events-none"
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-gray-900">{persona.name}</p>
                  <Badge className="bg-primary/10 text-primary text-[10px]">
                    {persona.capabilityType}
                  </Badge>
                </div>
                <p className="text-xs text-gray-500 mt-0.5">{persona.role}</p>
                <p className="text-xs text-gray-400 mt-0.5 truncate">
                  {persona.industryId} &middot; {persona.subcategoryId}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderConfirmStep = () => (
    <div className="space-y-6">
      {/* Business name */}
      <div>
        <label className="text-sm font-semibold text-gray-700 mb-2 block">
          Business Name (optional)
        </label>
        <p className="text-xs text-gray-500 mb-2">
          Applied as a suffix to all genie names, e.g. "The Night Owl - Smith Plumbing"
        </p>
        <Input
          placeholder="e.g. Smith Plumbing"
          value={businessName}
          onChange={(e) => setBusinessName(e.target.value)}
        />
      </div>

      {/* Selected personas with optional name overrides */}
      <div>
        <label className="text-sm font-semibold text-gray-700 mb-2 block">
          Genies to create ({selectedPersonasList.length})
        </label>
        <div className="space-y-2">
          {selectedPersonasList.map((persona) => (
            <div
              key={persona.id}
              className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">{persona.name}</p>
                <p className="text-xs text-gray-500">{persona.role}</p>
              </div>
              <Input
                placeholder="Custom name (optional)"
                value={nameOverrides[persona.id] || ""}
                onChange={(e) =>
                  setNameOverrides((prev) => ({ ...prev, [persona.id]: e.target.value }))
                }
                className="w-48 text-xs"
              />
              <button
                onClick={() => togglePersona(persona.id)}
                className="text-gray-400 hover:text-red-500 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Summary */}
      <div className="bg-genie-primary/5 p-4 rounded-lg border border-genie-primary/20">
        <p className="text-sm text-gray-900">
          <strong>Summary:</strong> Creating {selectedPersonasList.length} genie
          {selectedPersonasList.length !== 1 ? "s" : ""} for{" "}
          <strong>{selectedUserLabel}</strong>
          {businessName && (
            <> with business name <strong>"{businessName}"</strong></>
          )}
        </p>
      </div>

      {/* Progress (visible during creation) */}
      {isCreating && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-genie-primary" />
            <span className="text-sm text-gray-600">
              Creating {progress.current} of {progress.total}...
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-genie-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%` }}
            />
          </div>
          {progress.results.length > 0 && (
            <div className="space-y-1">
              {progress.results.map((r) => (
                <div key={r.personaId} className="flex items-center gap-2 text-xs">
                  {r.success ? (
                    <Check className="h-3 w-3 text-green-600" />
                  ) : (
                    <X className="h-3 w-3 text-red-500" />
                  )}
                  <span className={r.success ? "text-gray-600" : "text-red-600"}>
                    {r.personaName}{r.error ? ` - ${r.error}` : ""}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  // =========================================================================
  // Footer
  // =========================================================================

  const renderFooter = () => {
    if (step === "personas") {
      return (
        <div className="sticky bottom-0 border-t px-6 sm:px-8 py-4 flex justify-start gap-3 bg-white">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={() => setStep("confirm")}
            disabled={selectedPersonas.size === 0}
            className="bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white font-semibold px-6 shadow-lg shadow-genie-primary/30 hover:shadow-xl hover:shadow-genie-primary/40 hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:hover:scale-100 disabled:hover:shadow-lg"
          >
            Review ({selectedPersonas.size})
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      );
    }

    // Confirm step
    const isComplete = progress.current === progress.total && progress.total > 0;

    return (
      <div className="sticky bottom-0 border-t px-6 sm:px-8 py-4 flex justify-start gap-3 bg-white">
        {!isComplete ? (
          <>
            <Button variant="outline" onClick={() => setStep("personas")} disabled={isCreating}>
              Back
            </Button>
            <Button
              onClick={handleCreate}
              disabled={isCreating || selectedPersonas.size === 0}
              className="bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white font-semibold px-6 shadow-lg shadow-genie-primary/30 hover:shadow-xl hover:shadow-genie-primary/40 hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:hover:scale-100 disabled:hover:shadow-lg"
            >
              {isCreating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Creating...
                </>
              ) : (
                <>
                  <Zap className="h-4 w-4 mr-2" />
                  Create {selectedPersonas.size} Genie{selectedPersonas.size !== 1 ? "s" : ""}
                </>
              )}
            </Button>
          </>
        ) : (
          <Button
            onClick={handleClose}
            className="bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white font-semibold px-6 shadow-lg shadow-genie-primary/30 hover:shadow-xl hover:shadow-genie-primary/40 hover:scale-105 transition-all duration-200"
          >
            Done
          </Button>
        )}
      </div>
    );
  };

  // =========================================================================
  // Main render
  // =========================================================================

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <SheetContent
        side="right"
        showClose={false}
        className="w-full sm:max-w-2xl md:max-w-3xl lg:max-w-4xl flex flex-col h-full p-0"
      >
        {renderHeader()}
        <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-6">
          {step === "personas" && renderPersonaStep()}
          {step === "confirm" && renderConfirmStep()}
        </div>
        {renderFooter()}
      </SheetContent>
    </Sheet>
  );
};

export default QuickCreateSheet;
